import {
  createContext,
  useContext,
  useRef,
  useEffect,
  useCallback,
  memo,
} from "react";
import { Toast } from "primereact/toast";
import toastService from "../api/service";
import { IShowNotificationParams, ToastContextType } from "../api/types";
import { getSeverity } from "../lib/get-severity";
import { Inplace, InplaceContent, InplaceDisplay } from "primereact/inplace";
import { debounce } from "lodash";
// Создаем контекст
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Хук для использования контекста
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  return context;
};

// Компонент-провайдер для Toast
export const ToastProvider = memo(({ children }: any) => {
  const toastRef = useRef<Toast>(null);

  const getAdditionalParams = (params: IShowNotificationParams) => {
    const [beforeCut, afterCut] = params.message.split("cut_open");
    const isCut = params.message.includes("cut_open");

    return {
      beforeCut: isCut ? beforeCut : params.message,
      afterCut: isCut ? afterCut : null,
    };
  };
  const showMessage = useCallback(
    debounce((params: IShowNotificationParams) => {
      toastRef.current?.show({
        severity: getSeverity(params.type) as
          | "success"
          | "info"
          | "warn"
          | "error",
        summary: params.caption,
        detail: params.message,
        life: params.timeOut * 1000,
        content: params.message.includes("cut_open") && (
          <>
            {/* SVG и остальная разметка */}
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="p-icon p-toast-message-icon"
              aria-hidden="true"
              data-pc-section="icon"
            >
              {/* Ваш путь SVG */}
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.11101 12.8203C4.26215 13.5895 5.61553 14 7 14C8.85652 14 10.637 13.2625 11.9497 11.9497C13.2625 10.637 14 8.85652 14 7C14 5.61553 13.5895 4.26215 12.8203 3.11101C12.0511 1.95987 10.9579 1.06266 9.67879 0.532846C8.3997 0.00303296 6.99224 -0.13559 5.63437 0.134506C4.2765 0.404603 3.02922 1.07129 2.05026 2.05026C1.07129 3.02922 0.404603 4.2765 0.134506 5.63437C-0.13559 6.99224 0.00303296 8.3997 0.532846 9.67879C1.06266 10.9579 1.95987 12.0511 3.11101 12.8203ZM3.75918 2.14976C4.71846 1.50879 5.84628 1.16667 7 1.16667C8.5471 1.16667 10.0308 1.78125 11.1248 2.87521C12.2188 3.96918 12.8333 5.45291 12.8333 7C12.8333 8.15373 12.4912 9.28154 11.8502 10.2408C11.2093 11.2001 10.2982 11.9478 9.23232 12.3893C8.16642 12.8308 6.99353 12.9463 5.86198 12.7212C4.73042 12.4962 3.69102 11.9406 2.87521 11.1248C2.05941 10.309 1.50384 9.26958 1.27876 8.13803C1.05367 7.00647 1.16919 5.83358 1.61071 4.76768C2.05222 3.70178 2.79989 2.79074 3.75918 2.14976ZM7.00002 4.8611C6.84594 4.85908 6.69873 4.79698 6.58977 4.68801C6.48081 4.57905 6.4187 4.43185 6.41669 4.27776V3.88888C6.41669 3.73417 6.47815 3.58579 6.58754 3.4764C6.69694 3.367 6.84531 3.30554 7.00002 3.30554C7.15473 3.30554 7.3031 3.367 7.4125 3.4764C7.52189 3.58579 7.58335 3.73417 7.58335 3.88888V4.27776C7.58134 4.43185 7.51923 4.57905 7.41027 4.68801C7.30131 4.79698 7.1541 4.85908 7.00002 4.8611ZM7.00002 10.6945C6.84594 10.6925 6.69873 10.6304 6.58977 10.5214C6.48081 10.4124 6.4187 10.2652 6.41669 10.1111V6.22225C6.41669 6.06754 6.47815 5.91917 6.58754 5.80977C6.69694 5.70037 6.84531 5.63892 7.00002 5.63892C7.15473 5.63892 7.3031 5.70037 7.4125 5.80977C7.52189 5.91917 7.58335 6.06754 7.58335 6.22225V10.1111C7.58134 10.2652 7.51923 10.4124 7.41027 10.5214C7.30131 10.6304 7.1541 10.6925 7.00002 10.6945Z"
                fill="currentColor"
              ></path>
            </svg>
            <div className="p-toast-message-text" data-pc-section="text">
              <span className="p-toast-summary" data-pc-section="summary">
                Информация
              </span>
              <div className="p-toast-detail" data-pc-section="detail">
                {getAdditionalParams(params).beforeCut}
              </div>
              <div className="pt-2">
                <Inplace unstyled>
                  <InplaceDisplay>Подробнее...</InplaceDisplay>
                  <InplaceContent>
                    <p className="m-0">
                      {getAdditionalParams(params).afterCut}
                    </p>
                  </InplaceContent>
                </Inplace>
              </div>
            </div>
          </>
        ),
      });
    }, 150),
    []
  );

  // Устанавливаем функцию showMessage в toastService при монтировании компонента
  useEffect(() => {
    toastService.setShowMessage(showMessage);
  }, [showMessage]);

  return (
    <ToastContext.Provider value={{ showMessage }}>
      {children}
      <Toast ref={toastRef} position="bottom-right" />
    </ToastContext.Provider>
  );
});
